/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Loader2, Users, Building2, Activity, Database, FileText, Brain, HardDrive } from 'lucide-react'

export default function AnalyticsDashboard({ isAnalyticsFetching, analyticsData }) {
    // Use analytics data if available, otherwise fall back to placeholder data
    const dashboardData = React.useMemo(() => {
        if (!analyticsData) return {
            storage: { totalGB: 0, instances: 0 },
            studies: { total: 0, recent: 0 },
            reports: { total: 0, recent: 0, completed: 0, pending: 0, completionRate: 0 },
            aiAnalysis: { totalAnalyses: 0, abnormalFindings: 0, tbDetections: 0, detectionRate: 0 },
            users: { total: 0 },
            system: null,
        };

        return {
            ...analyticsData,
            users: {
                // Admin response uses system.totalUsers, regular uses users.total
                total: analyticsData.system?.totalUsers ?? analyticsData.users?.total ?? 0,
            },
        };
    }, [analyticsData]);

    const isAdmin = !!analyticsData?.system;

    const metrics = [
        {
            title: "Total Users",
            value: dashboardData.users?.total || 0,
            icon: Users,
            description: "Active users in system",
            trend: "Active users"
        },
        {
            title: "Total Studies",
            value: dashboardData.studies?.total || 0,
            icon: Building2,
            description: "Total studies",
            trend: `${dashboardData.studies?.recent || 0} recent`
        },
        {
            title: "Storage Used",
            value: `${dashboardData.storage?.totalGB?.toFixed(2) || 0} GB`,
            icon: HardDrive,
            description: "Total storage used",
            trend: `${dashboardData.storage?.instances || 0} instances`
        },
        {
            title: "Reports",
            value: dashboardData.reports?.total || 0,
            icon: FileText,
            description: "Total reports",
            trend: `${dashboardData.reports?.completionRate || 0}% completion`
        }
    ]

    const aiMetrics = [
        {
            title: "AI Analyses",
            value: dashboardData.aiAnalysis?.totalAnalyses || 0,
            icon: Brain,
            description: "Total AI analyses performed",
            trend: `${dashboardData.aiAnalysis?.detectionRate || 0}% detection rate`
        },
        {
            title: "Abnormal Findings",
            value: dashboardData.aiAnalysis?.abnormalFindings || 0,
            icon: Activity,
            description: "Abnormal findings detected",
            trend: "AI detected"
        }
    ]

    const reportMetrics = [
        {
            title: "Completed Reports",
            value: dashboardData.reports?.completed || 0,
            icon: FileText,
            description: "Signed off reports",
            trend: "Completed"
        },
        {
            title: "Pending Reports",
            value: dashboardData.reports?.pending || 0,
            icon: Activity,
            description: "Reports in progress",
            trend: "In progress"
        }
    ]

    return (
        <div className="h-full overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-2xl font-bold">Analytics Dashboard</h3>
                    <p className="text-muted-foreground">System overview and key metrics</p>
                </div>
                <Badge variant="outline" className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    System Status: Active
                </Badge>
            </div>

            {isAnalyticsFetching ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : (
                <>
                    {/* Main Metrics Grid */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {metrics.map((metric, index) => (
                            <Card key={index}>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        {metric.title}
                                    </CardTitle>
                                    <metric.icon className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{metric.value}</div>
                                    <p className="text-xs text-muted-foreground">
                                        {metric.description}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        {metric.trend}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* AI Analysis Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle>AI Analysis</CardTitle>
                            <CardDescription>
                                Artificial Intelligence analysis metrics
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2">
                                {aiMetrics.map((metric, index) => (
                                    <div key={index} className="flex items-center space-x-4">
                                        <metric.icon className="h-8 w-8 text-blue-500" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{metric.title}</p>
                                            <p className="text-2xl font-bold">{metric.value}</p>
                                            <p className="text-xs text-muted-foreground">{metric.description}</p>
                                            <p className="text-xs text-blue-600">{metric.trend}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Reports Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Reports Overview</CardTitle>
                            <CardDescription>
                                Report completion and status metrics
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2">
                                {reportMetrics.map((metric, index) => (
                                    <div key={index} className="flex items-center space-x-4">
                                        <metric.icon className="h-8 w-8 text-green-500" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{metric.title}</p>
                                            <p className="text-2xl font-bold">{metric.value}</p>
                                            <p className="text-xs text-muted-foreground">{metric.description}</p>
                                            <p className="text-xs text-green-600">{metric.trend}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Completion Rate</span>
                                    <span className="text-sm font-bold text-green-600">
                                        {dashboardData.reports?.completionRate || 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                    <div 
                                        className="bg-green-600 h-2 rounded-full" 
                                        style={{ width: `${dashboardData.reports?.completionRate || 0}%` }}
                                    ></div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Storage Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Storage Overview</CardTitle>
                            <CardDescription>
                                System storage and instance metrics
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="flex items-center space-x-4">
                                    <HardDrive className="h-8 w-8 text-purple-500" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">Total Storage</p>
                                        <p className="text-2xl font-bold">{dashboardData.storage?.totalGB?.toFixed(2) || 0} GB</p>
                                        <p className="text-xs text-muted-foreground">Storage used</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <Database className="h-8 w-8 text-orange-500" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">Total Instances</p>
                                        <p className="text-2xl font-bold">{dashboardData.storage?.instances || 0}</p>
                                        <p className="text-xs text-muted-foreground">Medical instances</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    )
} 